import React from "react";
import { Chip } from "./Chip";
import { Cluster } from "./Stack";
import { COUNT_TEXT } from "./SectionRule";

/// One line of a "here is what this will do" dialog: a tinted icon, the count,
/// what it counts, and the consequence beneath. Mirrors the publish-validation
/// sections so a destructive review and a blocked publish read the same way.

export const ImpactRow: React.FC<{
  icon: React.ReactNode;
  count?: number;
  label: string;
  hint?: string;
  tone?: "default" | "warning";
  /// Gray label, for a list read as detail under a primary sentence.
  quiet?: boolean;
  /// What the row is about, listed under it — e.g. the tables you can rescue.
  children?: React.ReactNode;
}> = ({ icon, count, label, hint, tone = "default", quiet, children }) => (
  <div className="flex gap-2">
    {/* The icon's own width, one label line tall: flush with the dialog's text
        edge and centred on the label, not on the row with its hint. */}
    <span
      className={`flex w-4 shrink-0 items-center ${
        tone === "warning" ? "text-warning" : "text-fg-muted"
      }`}
      style={{ fontSize: "var(--font-size)", height: "1lh" }}
    >
      {icon}
    </span>
    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className={quiet ? "text-fg-muted-medium" : "text-fg-medium"}>
        {count !== undefined && (
          <span
            className="font-mono"
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {count}{" "}
          </span>
        )}
        {label}
      </span>
      {hint && <span className="text-fg-muted-regular-small">{hint}</span>}
      {children}
    </span>
  </div>
);

/// What an impact row names, as chips: past `max`, the rest fold into one
/// "+N" chip. Each label is one centred line, so text and icons sit level.
export const ImpactChips: React.FC<{
  items: { key: string; label: React.ReactNode }[];
  max?: number;
}> = ({ items, max = 6 }) => (
  <Cluster gap="xs" className="pt-1">
    {items.slice(0, max).map((item) => (
      <Chip
        key={item.key}
        size="sm"
        label={
          <span className="inline-flex items-center gap-1.5 leading-none text-fg-muted">
            {item.label}
          </span>
        }
      />
    ))}
    {items.length > max && (
      <Chip
        size="sm"
        label={
          <span className={`leading-none ${COUNT_TEXT}`}>
            +{items.length - max}
          </span>
        }
      />
    )}
  </Cluster>
);

/// A count with its noun as a small icon (`9 ▦`), read out as `label`.
export const IconCount: React.FC<{
  count: number;
  icon: React.ReactNode;
  label: string;
}> = ({ count, icon, label }) => (
  <span
    role="img"
    aria-label={label}
    className={`inline-flex items-center gap-0.5 leading-none ${COUNT_TEXT}`}
  >
    {count}
    {icon}
  </span>
);

/// `1 space` / `2 spaces`, for the labels above.
export const plural = (n: number, word: string, suffix = "s") =>
  n === 1 ? word : `${word}${suffix}`;
